/**
 * The two query-string keys the page templates put in the URL — the designer's brief part 2
 * §3, §4 and §7.1, `docs/adr/9032`. Pure parses and writes over a search string, so a test asserts
 * on a string and never on `window` (CLAUDE.md §3.4, rule 4).
 *
 *   `?row=`   the row a queue or a «Perché» page has open — so the explanation, or the decision,
 *             can be linked to and a reload lands on the same row
 *   `?sort=`  the sort of a virtualised grid — `<column>:asc` or `<column>:desc`
 *
 * A template never writes the URL itself: it calls its `onSelectRow` / `onSortChange`, and the
 * page writes the key with these helpers. No key means no row and no sort: nothing is chosen for
 * the planner (DEC-85).
 */

import { useEffect, useState } from 'react';

export const ROW_PARAM = 'row';
export const SORT_PARAM = 'sort';

/** The row named in `search`, or `null`. */
export function readRowParam(search: string): string | null {
  const row = new URLSearchParams(search).get(ROW_PARAM);
  return row !== null && row.trim() !== '' ? row : null;
}

/**
 * `search` with `key` set to `value` (or removed, for `null`), every other key kept — the scope a
 * page was opened with (`?runId=`, `?country=`) travels with the row.
 */
export function withParam(search: string, key: string, value: string | null): string {
  const params = new URLSearchParams(search);
  if (value === null) params.delete(key);
  else params.set(key, value);
  const next = params.toString();
  return next === '' ? '' : `?${next}`;
}

/** A path with `search`'s keys and `?row=` set — the link to a row on another page. */
export function rowHref(path: string, search: string, rowId: string): string {
  return `${path}${withParam(search, ROW_PARAM, rowId)}`;
}

export type SortDirection = 'asc' | 'desc';

export interface GridSort {
  readonly column: string;
  readonly direction: SortDirection;
}

/** The sort named in `search` (`?sort=net_benefit:desc`), or `null` for none or a malformed one. */
export function readSortParam(search: string): GridSort | null {
  const raw = new URLSearchParams(search).get(SORT_PARAM);
  if (raw === null) return null;
  const at = raw.lastIndexOf(':');
  if (at <= 0) return null;
  const column = raw.slice(0, at);
  const direction = raw.slice(at + 1);
  return direction === 'asc' || direction === 'desc' ? { column, direction } : null;
}

/** The `?sort=` value for a sort. */
export function sortParamValue(sort: GridSort): string {
  return `${sort.column}:${sort.direction}`;
}

/**
 * Follow an in-app href: a history entry and the `popstate` the shell listens for, so the router
 * re-resolves and the back button returns here. The shared twin of the chapters' own
 * `navigateInApp`, for the templates' links.
 */
export function navigateInApp(href: string): void {
  if (typeof window === 'undefined') return;
  window.history.pushState({}, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/**
 * The address bar's query, following back and forward — the one read a page needs to parse its
 * `?row=` and `?sort=` with the pure helpers above. Re-renders on the `popstate` every in-app
 * navigation dispatches.
 */
export function useLocationSearch(): string {
  const [search, setSearch] = useState(() => (typeof window === 'undefined' ? '' : window.location.search));
  useEffect(() => {
    const onPopState = (): void => setSearch(window.location.search);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  return search;
}

/**
 * The page's answer to a template's `onSelectRow`: `?row=` set on the current address, every
 * other key kept, as a history entry — so the back button walks the rows the planner opened and a
 * copied link opens the same one.
 */
export function selectRowInUrl(rowId: string): void {
  if (typeof window === 'undefined') return;
  navigateInApp(`${window.location.pathname}${withParam(window.location.search, ROW_PARAM, rowId)}`);
}
